import { useMemo, useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Loading from '../../../components/globales/Loading';
import TablaLayout from '../../../components/globales/TablaLayout';
import ModalDynamic from '../../../components/globales/ModalDynamic';
import useCrud from '../../../hooks/useCrud';
import ENDPOINTS from '../../../api/endpoints';
import { preguntaSchema } from '../../../services/validators';
import axios from '../../../axiosConfig';

export default function Preguntas() {
    const { id } = useParams();
    const navigate = useNavigate();
    const keyField = 'id';

    const preguntasEndpoints = useMemo(() => ({
        listar: ENDPOINTS.encuestasPreguntas.listar(id),
        crear: ENDPOINTS.encuestasPreguntas.crear,
        actualizar: ENDPOINTS.encuestasPreguntas.actualizar,
        borrar: ENDPOINTS.encuestasPreguntas.borrar
    }), [id]);

    const initForm = () => ({
        enunciado: '',
        tema: '',
        numero_opciones_respuesta: '',
        porcentaje: '',
        idEncuesta: parseInt(id, 10)
    });

    const [temas, setTemas] = useState([]);
    
    // Cargar temas desde el endpoint de listar
    useEffect(() => {
        const cargarTemas = async () => {
            try {
                const response = await axios.get(ENDPOINTS.encuestasPreguntas.listar(id));
                const temasData = response.data.temas || [];
                const temasOptions = temasData.map(tema => ({
                    value: String(tema.id),
                    label: tema.nombre
                }));
                setTemas(temasOptions);
            } catch (error) {
                // Error silencioso para no mostrar logs en producción
            }
        };
        
        cargarTemas();
    }, [id]);

    const columns = [
        { label: 'No', key: 'index', align: 'center', render: (_, i) => i + 1 },
        { label: 'Enunciado', key: 'enunciado', width: '25%' },
        { 
            label: 'Tema', 
            key: 'tema', 
            width: '25%',
            options: temas,
            render: (_, idx, item) => {
                // En la tabla, el campo viene como id_tema
                const temaId = item.id_tema || item.tema;
                const tema = temas.find(t => t.value === String(temaId));
                return tema ? tema.label : temaId;
            }
        },
        { label: 'Opciones de Respuesta', key: 'numero_opciones_respuesta', type: 'number', align: 'center', width: '20%',
            render: (_, idx, item) => {
                const value = item.numero_opciones_respuesta;
                return value !== undefined && value !== null ? String(value) : '0';
            }
        },
        { label: 'Porcentaje (Encuesta)', key: 'porcentaje', type: 'number', align: 'center', width: '20%',
            render: (_, idx, item) => {
                const value = item.porcentaje;
                if (value !== undefined && value !== null) {
                    // Si es un número decimal, formatearlo a 2 decimales
                    const numValue = parseFloat(value);
                    return isNaN(numValue) ? String(value) : numValue.toFixed(2);
                }
                return '0.00';
            }
        },
    ];

    // Configuración para el formulario del modal
    const formColumns = [
        { 
            label: 'Enunciado', 
            key: 'enunciado', 
            type: 'text', 
            required: true, 
            multiline: true, 
            size: 'large'
        },
        {
            key: 'tema',
            label: 'Tema',
            type: 'select',
            required: true,
            options: temas
        },
        { 
            label: 'Número Opciones de Respuesta', 
            key: 'numero_opciones_respuesta', 
            type: 'number', 
            required: true
        },
        { 
            label: 'Porcentaje (Encuesta)', 
            key: 'porcentaje', 
            type: 'number', 
            required: true
        },
    ];

    const breadcrumbs = [
        { label: 'Inicio', href: '/inicio' },
        { label: 'Encuestas', href: '/encuestas' },
        { label: 'Preguntas' }
    ];

    const {
        data,
        loading,
        error,
        showModal,
        actionType,
        formData,
        setFormData,
        errors,
        openModal,
        closeModal,
        handleChange,
        submit,
        remove
    } = useCrud(preguntasEndpoints, { keyField, initForm, columns: formColumns, schema: preguntaSchema });

    // Normalizar los datos cuando se abra el modal de editar
    useEffect(() => {
        if (showModal && actionType === 'editar' && formData) {
            let needsUpdate = false;
            const updates = {};

            // Mapear id_tema a tema para el formulario
            if (formData.id_tema !== undefined && formData.tema === undefined) {
                updates.tema = String(formData.id_tema);
                needsUpdate = true;
            }
            
            // Asegurar que idEncuesta esté presente
            if (!formData.idEncuesta && formData.id_encuesta) {
                updates.idEncuesta = parseInt(formData.id_encuesta, 10);
                needsUpdate = true;
            }

            // Asegurar que numero_opciones_respuesta esté presente como string
            if (formData.numero_opciones_respuesta !== undefined && formData.numero_opciones_respuesta !== null && typeof formData.numero_opciones_respuesta !== 'string') {
                updates.numero_opciones_respuesta = String(formData.numero_opciones_respuesta);
                needsUpdate = true;
            }

            // Asegurar que porcentaje esté presente como string
            if (formData.porcentaje !== undefined && formData.porcentaje !== null && typeof formData.porcentaje !== 'string') {
                updates.porcentaje = String(formData.porcentaje);
                needsUpdate = true;
            }

            if (needsUpdate) {
                setFormData(prev => ({
                    ...prev,
                    ...updates
                }));
            }
        }
    }, [showModal, actionType, formData, setFormData]);

    if (loading) return <Loading mensaje="Cargando Preguntas..." />;
    if (error) return <div className="text-danger">Error: {error.message}</div>;

    return (
        <div className="container">
            <TablaLayout
                title="Preguntas"
                data={data}
                columns={columns}
                keyField={keyField}
                breadcrumbs={breadcrumbs}
                onCreate={() => openModal('crear')}
                onView={item => openModal('consultar', item)}
                onEdit={item => openModal('editar', item)}
                onDelete={remove}
                onNavigate={item => navigate(`/respuestas/${item[keyField]}`)}
                navigateTitle="Agregar Respuestas"
                navigateIcon="note_add"
                disableDelete={item => item.registros_respuestas > 0}
                showConsult={false}
            />

            <ModalDynamic
                show={showModal}
                accion={actionType}
                title="Pregunta"
                columns={formColumns}
                formData={formData}
                errors={errors}
                onChange={handleChange}
                onSubmit={submit}
                onClose={closeModal}
            />
        </div>
    );
}
